import { saveLoginSession } from '../api/auth';
import { applyThemeAccent } from '../api/theme';

const AUTH_MODES = ['none', 'tokens', 'ticket', 'cookie', 'redirect'];
export const HOST_PRESENTATION_CHANGE_EVENT = 'bandhubuy-im:host-presentation-change';
export const HOST_AUTH_UPDATE_EVENT = 'bandhubuy-im:host-auth-update';
export const HOST_LOGOUT_EVENT = 'bandhubuy-im:host-logout';
export const HOST_DEACTIVATE_EVENT = 'bandhubuy-im:host-deactivate';
export const CHILD_AUTH_UPDATED_EVENT = 'bandhubuy-im:child-auth-updated';
export const CHILD_LOGOUT_EVENT = 'bandhubuy-im:child-logout';
export const CHILD_UNREAD_COUNT_EVENT = 'bandhubuy-im:unread-count-change';
export const HOST_THEME_ACCENTS = {
  sheep: '#806af6',
  element: '#409eff',
  classical: '#596a80',
};
const HOST_THEME_NAVIGATION_BACKGROUNDS = {
  sheep: '#e1e4ff',
  element: '#deebf7',
  classical: '#e4e8ec',
};
const LOCALE_MAP = {
  'zh-CN': 'zh',
  'en-US': 'en',
};

function getRawHostContext() {
  if (typeof window === 'undefined' || !window.$wujie || !window.$wujie.props) {
    return null;
  }
  return window.$wujie.props;
}

export function isWujieEmbedded() {
  const context = getRawHostContext();
  return !!(context && context.embed === true);
}

export function getHostContext() {
  const context = getRawHostContext();
  if (!context || context.version !== 1 || context.embed !== true) {
    return null;
  }

  const auth = context.auth || {};
  return {
    version: context.version,
    embed: true,
    locale: LOCALE_MAP[context.locale] || context.locale,
    theme: context.theme,
    navigationBackground: context.navigationBackground,
    auth: {
      mode: AUTH_MODES.includes(auth.mode) ? auth.mode : 'none',
      payload: auth.payload || null,
    },
    openAdminResource: typeof context.openAdminResource === 'function' ? context.openAdminResource : null,
    resolveStoreDetailByPhoneNo:
      typeof context.resolveStoreDetailByPhoneNo === 'function' ? context.resolveStoreDetailByPhoneNo : null,
  };
}

export function applyHostAuthentication(context = getHostContext()) {
  if (!context || context.auth.mode !== 'tokens') return false;

  const { accessToken, refreshToken } = context.auth.payload || {};
  if (!accessToken || !refreshToken) return false;

  const ownerId = context.auth.payload?.ownerId ?? null;
  const currentOwnerId = sessionStorage.getItem('ownerId');
  if (currentOwnerId && ownerId && String(currentOwnerId) !== String(ownerId)) {
    clearRuntimeSession();
  }

  saveLoginSession(
    { accessToken, refreshToken },
    {
      autoLogin: false,
      ownerId,
    },
  );
  if (ownerId !== null && ownerId !== undefined) {
    sessionStorage.setItem('ownerId', String(ownerId));
  }
  return true;
}

function getWujieBus() {
  return typeof window === 'undefined' || !window.$wujie ? null : window.$wujie.bus;
}

function clearRuntimeSession() {
  sessionStorage.removeItem('accessToken');
  sessionStorage.removeItem('refreshToken');
  sessionStorage.removeItem('ownerId');
}

export function subscribeHostAuthUpdate(handler) {
  const bus = getWujieBus();
  if (!bus || typeof bus.$on !== 'function' || typeof handler !== 'function') return () => {};
  bus.$on(HOST_AUTH_UPDATE_EVENT, handler);
  return () => bus.$off?.(HOST_AUTH_UPDATE_EVENT, handler);
}

export function subscribeHostLogout(handler) {
  const bus = getWujieBus();
  if (!bus || typeof bus.$on !== 'function' || typeof handler !== 'function') return () => {};
  bus.$on(HOST_LOGOUT_EVENT, handler);
  return () => bus.$off?.(HOST_LOGOUT_EVENT, handler);
}

export function notifyHostLogout() {
  const bus = getWujieBus();
  if (!bus) return;
  bus.$emit(CHILD_LOGOUT_EVENT, {
    ownerId: sessionStorage.getItem('ownerId') || getHostContext()?.auth?.payload?.ownerId || null,
  });
}

export function subscribeHostDeactivate(handler) {
  const bus = getWujieBus();
  if (!bus || typeof bus.$on !== 'function' || typeof handler !== 'function') return () => {};
  bus.$on(HOST_DEACTIVATE_EVENT, handler);
  return () => bus.$off?.(HOST_DEACTIVATE_EVENT, handler);
}

export function notifyHostUnreadCount(count) {
  const bus = getWujieBus();
  if (!bus || typeof bus.$emit !== 'function') return;
  bus.$emit(CHILD_UNREAD_COUNT_EVENT, {
    ownerId: sessionStorage.getItem('ownerId') || getHostContext()?.auth?.payload?.ownerId || null,
    count: Math.max(0, Number(count) || 0),
  });
}

export function applyHostPresentation(context = getHostContext()) {
  if (!context || !context.theme) return;

  document.documentElement.setAttribute('data-host-theme', context.theme);
  const themeAccent = HOST_THEME_ACCENTS[context.theme];
  if (themeAccent) applyThemeAccent(themeAccent);
  applyNavigationBackground(context.navigationBackground || HOST_THEME_NAVIGATION_BACKGROUNDS[context.theme], themeAccent);
}

function applyNavigationBackground(background, themeAccent) {
  if (typeof background !== 'string' || !/^#[0-9a-fA-F]{6}$/.test(background)) return;

  const root = document.documentElement;
  const red = parseInt(background.slice(1, 3), 16);
  const green = parseInt(background.slice(3, 5), 16);
  const blue = parseInt(background.slice(5, 7), 16);
  const isLight = red * 0.299 + green * 0.587 + blue * 0.114 > 160;

  root.style.setProperty('--im-host-navigation-background', background);
  root.style.setProperty('--im-host-navigation-text-color', isLight ? '#4a5568' : 'rgba(255, 255, 255, 0.82)');
  root.style.setProperty('--im-host-navigation-active-color', isLight ? themeAccent || '#2830d3' : '#ffffff');
  root.style.setProperty('--im-host-navigation-hover-background', isLight ? 'rgba(255, 255, 255, 0.52)' : 'rgba(255, 255, 255, 0.08)');
  root.style.setProperty('--im-host-navigation-active-background', isLight ? 'rgba(255, 255, 255, 0.78)' : 'rgba(255, 255, 255, 0.14)');
}

export function subscribeHostPresentationChange(handler) {
  const bus = typeof window === 'undefined' || !window.$wujie ? null : window.$wujie.bus;
  if (!bus || typeof bus.$on !== 'function' || typeof handler !== 'function') {
    return () => {};
  }

  const listener = (presentation) => {
    if (!presentation || typeof presentation !== 'object') return;

    handler({
      locale: LOCALE_MAP[presentation.locale] || presentation.locale,
      theme: presentation.theme,
      navigationBackground: presentation.navigationBackground,
    });
  };

  bus.$on(HOST_PRESENTATION_CHANGE_EVENT, listener);
  return () => {
    if (typeof bus.$off === 'function') bus.$off(HOST_PRESENTATION_CHANGE_EVENT, listener);
  };
}

export function openAdminResource(payload) {
  const context = getHostContext();
  if (!context || typeof context.openAdminResource !== 'function') return false;
  return context.openAdminResource(payload) === true;
}
